import { Component, OnInit, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../../../services/language'
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import { SecurityService } from '../../../../services/security.service';
import moment from 'moment';
import { Subject } from "rxjs";
import { ago, daysAgo } from 'src/app/utils/date';
import { formatPhone } from 'src/app/utils/format'
import { Contact } from 'src/app/models/contact.model';
import { paymentCategoryIcon } from 'src/app/utils/icons';

@Component({
    selector: 'past-due-view',
    templateUrl: './past-due-view.component.html',
    styleUrls: ['./past-due-view.component.scss'],
    providers: [TitleCasePipe],
    standalone: false
})
export class PastDueViewComponent implements OnInit, OnChanges {

    /** Unique internal identity of the sale receivable whose collections will be shown. */
    @Input() termsId: number = 0

    /** Unique internal identity of the sale whose collections will be shown. */
    @Input() saleId: number = 0

    /** Unique internal identity of the contact/customer whose collections will be shown. */
    @Input() contactId: number = 0

    /** Handle payment taken notifications. */
    protected paymentTakenEvent: Subject<Object> = new Subject()

    /** Notify take payment that we want to collect a certain amount. */
    protected payThisAmountEvent: Subject<number> = new Subject()

    /** Currently selected lot identity. */
    private lotId: number = 0

    /** The collection setup for the lot, dealer or corporation. */
    protected setup: any = null

    /** Save the language */
    protected language: string= 'EN'

    /** Set all words */
    protected words = words.language

    // Details of the contact currently being viewed.
    protected contact: Contact | null = null

    // The full name of the contact whose payments need to be collected.
    protected contactName: string | null = null

    // The primary address of the contact whose payments need to be collected.
    protected address: string | null = null

    // The primary email address of the contact whose payments need to be collected.
    protected email: string | null = null

    // The primary phone/cell number of the contact whose payments need to be collected.
    protected phone: string | null = null

    //
    private records: number = 0

    //
    private page: number = 1

    // Indicates whether the data is currently loading.
    protected loading: boolean = false

    // Indicates whether any more data is available
    protected exhausted: boolean = false

    // Raw list of payments due soon for the given contact.
    private payments: Array<any> = []

    /**  */
    protected pays: Object | null = null

    // Groups of related payments so we can build tabs.
    protected groups: Object = {}

    get groupKeys() { return Object.keys(this.groups) }

    get pastDue() { 
        if (!this.pays || !this.pays['past'] || !this.pays['past']['total']) {
            return 0
        }

        return parseFloat(this.pays['past']['total'])
    }

    get pastDueSince() {
        if (!this.pays || !this.pays['past'] || !this.pays['past']['items'] || (this.pays['past']['items'].length <= 0)) {
            return 'as of today'
        }

        return `since ${moment(this.pays['past']['items'][0].due).format('ddd MMM Do, YYYY')}`
    }

    get pastDueCategories() {
        if (!this.pays || !this.pays['past'] || !this.pays['past']['categories'] || (this.pays['past']['categories'].length <= 0)) {
            return ''
        }

        return `${this.pays['past']['categories'].join(', ')}`
    }

    get dueToday() { 
        if (!this.pays || !this.pays['today'] || !this.pays['today']['total']) {
            return 0
        }

        return parseFloat(this.pays['today']['total'])
    }

    get allocation() {
        if (!this.setup || !this.setup.paymentAllocationOrder) {
            return []
        }

        const items = JSON.parse(this.setup.paymentAllocationOrder)
        if (!items || !Array.isArray(items) || (items.length <= 0)) {
            return []
        }

        const result = []
        for (const item of items) {
            result.push(item.name.toLowerCase())
        }

        return result
    }

    get formattedPhone() {
        if (!this.phone) {
            return null
        }

        return formatPhone(this.phone)
    }

    // **************************
    // ****** life cycles *******
    // **************************

    constructor(private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, protected security: SecurityService, private router: Router, private fb: FormBuilder, private titleCasePipe: TitleCasePipe) { }

    ngOnInit() {
        if (localStorage.getItem('lot')) {
            this.lotId = parseInt(localStorage.getItem('lot'))
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.lotId) {
            this.setup = null
            this.readSetup()
            this.reset()
            this.fetch()
        }

        if (changes.contactId) {
            this.contact = null
            this.contactName = null
            this.address = null
            this.email = null
            this.phone = null

            this.readContact()
            this.reset()
            this.fetch()
        }

        if (changes.saleId) {
            this.reset()
            this.fetch()
        }
    }

    private reset = (): void => {
        this.page = 1
        this.exhausted = false
        this.records = 0
    }

    // Listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeLot':
                this.lotId = parseInt(e['lot'])
                this.readSetup()
                break
        }
    }

    private readContact = (): void => {        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.contact) {
                return
            }
            
            this.contact = res.data.contact
            this.contactName = this.contact.name
            this.determineAddress(this.contact.contactAddresses)
            this.determineEmail(this.contact.contactCommunications)
            this.determinePhone(this.contact.contactCommunications)
        })
    }

    private determineAddress = (addresses) => {
        this.address = null
        if (!addresses || (addresses.length <= 0)) {
            return
        }

        const primary = addresses.find((addy) => addy.primaryAddress)
        if (!primary) {
            return
        }

        this.address = `${primary.address} ${this.titleCasePipe.transform(primary.city)}, ${primary.state} ${primary.zipCode}`
    }

    private determineEmail = (communications) => {
        this.email = null
        if (!communications || (communications.length <= 0)) {
            return
        }

        const primary = communications.find((comm) => ((comm.type === 'email') && comm.primary))
        if (!primary) {
            return
        }

        this.email = `${primary.value}`
    }

    private determinePhone = (communications) => {
        this.phone = null
        if (!communications || (communications.length <= 0)) {
            return
        }

        const primary = communications.find((comm) => ((comm.type === 'phone') && comm.primary))
        if (!primary) {
            return
        }

        this.phone = `${primary.value}`
    }

    /**
    * Read the collection setup for the lot, 
    * dealership or corp (what ever is available).
    */
    private readSetup = async () => {
        if (this.setup) {
            return
        }

        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        await this.master.getAsync(`lots/${this.lotId}/collectionSetup`).then(async (res) => {
            if (!res || !res.data || !res.data.collectionSetup) {
                this.setup = null
                return
            }

            this.setup = res.data.collectionSetup
        })
    }

    /** 
     * Fetch the the pending payments for the current contact.
    */
    private fetch = (): void => {
        if (this.loading) {
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        if (!this.termsId && (this.termsId <= 0)) {
            this.payments = []
            this.groups = {}
            this.pays = null
            return
        }

        this.loading = true
        if (this.page === 1) {
            this.payments = []
            this.groups = {}
            this.pays = null
        }
        
        this.master.get(`receivables/${this.termsId}`, (res) => {
            this.loading = false
  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
  
            this.records = 1
            this.exhausted = true
  
            this.payments = [res.data]
            this.constructGroups()
        })
    }

    /**
     * Groups payments into their respective categories and 
     * loans for the tabbed display in the UI.
     */
    private constructGroups = () => {
        if (!this.payments || (this.payments.length <= 0)) {
            this.groups = {}
            return
        }
        
        if (this.page <= 1) {
            this.groups = {}
        }

        const pays = {
            past: {
                total: 0,
                items: [],
                categories: []
            },
            today: {
                total: 0,
                items: []
            },
            next: {
                total: 0,
                items: []
            }
        }

        for (const payment of this.payments) {
            const key = `${payment.saleId || 0}-${payment.serviceId || 0}-${payment.insuranceId || 0}`
            if (!this.groups[key]) {
                this.groups[key] = {}
                this.groups[key].id = payment.id
                this.groups[key].payments = []
                this.groups[key].termsId = payment.termsId
                this.groups[key].terms = null
                this.groups[key].url = payment.url
                this.groups[key].vehicleId = payment.vehicleId
                this.groups[key].title = `${payment.contact}`
                this.groups[key].subtitle = ''
                this.groups[key].method = payment.method
                this.groups[key].sold = payment.sold
                this.groups[key].payoff = parseFloat(payment.payoff)
                this.groups[key].balance = parseFloat(payment.balance)
                this.groups[key].paymentBalance = parseFloat(payment.paymentBalance)
                this.groups[key].tax = parseFloat(payment.taxBalance)
                this.groups[key].apr = parseFloat(payment.apr)
                this.groups[key].payment = parseFloat(payment.payment)
                this.groups[key].unearnedInterest = parseFloat(payment.unearnedInterest)
                this.groups[key].unpaidEarnedInterest = parseFloat(payment.unpaidEarnedInterest)
                this.groups[key].saleId = payment.saleId
                this.groups[key].type = payment.type
                this.groups[key].financed = (payment.financed == true)
                this.groups[key].bhph = (payment.bhph == true)
                this.groups[key].quote = (payment.quote == true)
                this.groups[key].ridingCash = payment.ridingCash
                this.groups[key].lateFees = payment.lateFees
                this.groups[key].gpsCount = payment.gpsCount
                this.groups[key].cpiEnds = payment.cpiEnds
                this.groups[key].insuranceProvider = payment.insuranceProvider
                this.groups[key].insuranceExpires = payment.insuranceExpires
                this.groups[key].frequency = payment.frequency

                if (payment.vin) {
                    this.groups[key].title = `${payment.vehicle}`
                    this.groups[key].subtitle = `${payment.stock} | ${payment.vin}`
                }
            }

            if (parseFloat(payment.deposit || 0) > 0) {
                this.groups[key].payments.push({
                    id: parseInt(payment.saleId),
                    termsId: parseInt(payment.termsId),
                    due: payment.depositDue,
                    category: 'deposit',
                    amount: parseFloat(payment.deposit),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (parseFloat(payment.initial || 0) > 0) {
                this.groups[key].payments.push({
                    id: parseInt(payment.saleId),
                    termsId: parseInt(payment.termsId),
                    due: payment.initialDue,
                    category: 'initial down',
                    amount: parseFloat(payment.initial),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (parseFloat(payment.deferred || 0) > 0) {
                this.groups[key].payments.push({
                    id: parseInt(payment.saleId),
                    termsId: parseInt(payment.termsId),
                    due: payment.deferredDue,
                    category: 'deferred down',
                    amount: parseFloat(payment.deferred),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (!payment.quote) {
                this.groups[key].payments.push({
                    id: parseInt(payment.saleId),
                    termsId: parseInt(payment.termsId),
                    due: payment.loanDue || payment.due,
                    category: payment.bhph ? 'mv loan' : 'drive-out price',
                    amount: parseFloat(payment.loan || 0),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if ((parseFloat(payment.lateFees || 0) > 0) || (parseFloat(payment.lateFeesWaived || 0) > 0)) {
                this.groups[key].payments.push({
                    id: parseInt(payment.saleId),
                    termsId: parseInt(payment.termsId),
                    due: payment.lateFeesDue,
                    category: 'late fee',
                    amount: parseFloat(payment.lateFees),
                    waived: parseFloat(payment.lateFeesWaived),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            if (parseFloat(payment.cpi || 0) > 0) {
                this.groups[key].payments.push({
                    id: parseInt(payment.saleId),
                    termsId: parseInt(payment.termsId),
                    due: payment.cpiDue,
                    category: 'cpi',
                    amount: parseFloat(payment.cpi),
                    status: 'unpaid',
                    vehicle: payment.vehicle,
                    contact: payment.contact,
                    stock: payment.stock,
                    vin: payment.vin,
                    url: payment.url
                })
            }

            for (const item of this.groups[key].payments) {
                const timeline = ago(item.due)
                const cloned = JSON.parse(JSON.stringify(item))

                switch (timeline) {
                    case 'past':
                        pays.past.total += parseFloat(item.amount)
                        pays.past.items.push(cloned)
                        pays.past.categories.push(item.category)

                        pays.today.total += parseFloat(item.amount)
                        pays.today.items.push(cloned)
                        break
                    case 'today':
                        pays.today.total += parseFloat(item.amount)
                        pays.today.items.push(cloned)
                        break
                    default: // soon (next)
                        pays.next.total += parseFloat(item.amount)
                        pays.next.items.push(cloned)
                        break
                }
            }
        }

        this.pays = pays
    }

    /**
     * Trigger event to the take payment component with 
     * the current past due amount.
     * @param e Event meta data that triggered this function.
     */
    protected payPastDue = (e: Event) => {
        const toPay = this.pastDue
        if (toPay <= 0) {
            return
        }

        this.payThisAmountEvent.next(toPay)
    }

    /**
     * Trigger event to the take payment component with 
     * the current due today amount.
     * @param e Event meta data that triggered this function.
     */
    protected payDueToday = (e: Event) => {
        const toPay = this.dueToday
        if (toPay <= 0) {
            return
        }

        this.payThisAmountEvent.next(toPay)
    }

    /**
     * Listen for payment processing events from the take payment component then update 
     * the display and pass the changes to other components.
     * @param payment The payment details after processing.
     */
    protected paymentTaken = (payment: Object) => {
        this.paymentTakenEvent.next(payment)
        this.reset()
        this.fetch()
    }

    /**
     * Listen for note events from the notes component then update 
     * the display.
     * @param note The details of the note that was posted.
     */
    protected notePosted = (note: Object) => {
        this.reset()
        this.fetch()
    }

    /**
     * Listen for promise events from the promises component then update 
     * the display.
     * @param promise The details of the promise that was posted.
     */
    protected promiseAdded = (promise: Object) => {
        this.reset()
        this.fetch()
    }

    /**
     * Handle refund events from other components.
     * @param id Unique internal identity of the receipt or payment that was refunded.
     */
    protected refunded = (id: number): void => {
        if (!id || (id <= 0)) {
            return
        }

        this.paymentTakenEvent.next(null)
    }

    protected insuranceMessage = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'No insurance'
        }

        if (receivable.insuranceExpires && receivable.insuranceIsCpi) {
            return this.cpiMessage(receivable.insuranceExpires)
        }

        return this.insMessage(receivable.insuranceProvider, receivable.insuranceExpires)
    }

    protected insuranceClass = (receivable): string => {
        if (!receivable || !receivable.id) {
            return null
        }

        if (!receivable.insuranceExpires) {
            return 'danger'
        }

        return this.insClass(receivable.insuranceExpires)
    }

    private cpiMessage = (ends: string): string => {
        if (!ends) {
            return 'No CPI'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `CPI expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `CPI expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return 'CPI in good standing'
    }

    private insClass = (ends: string): string => {
        if (!ends) {
            return 'secondary'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 30) {
            return 'danger'
        }

        if (days <= 60) {
            return 'warning'
        }

        return 'success'
    }

    private insMessage = (provider: string, ends: string): string => {
        if (!ends) {
            return 'No insurance'
        }

        const today = moment().startOf('day')
        const days = moment(ends).startOf('day').diff(today, 'days')

        if (days <= 0) {
            return `Insurance expired ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        if (days <= 60) {
            return `Insurance expires ${moment(ends).startOf('day').format('MMM Do, YY')}`
        }

        return `${provider} active`
    }

    /**
     * Determine an tab icon to show based on the payment category.
     * @param category The payment category.
     */
    protected icon = (category: string) => {
        return paymentCategoryIcon(category)
    }

    /**
     * Open the contact editor in a modal.
     * @param e Event that triggered the contact edit.
     */
    protected edit = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const btn = (document.getElementById('editContactModalBtn_past-due') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Contact was modified in the modal.
     * @param {Object} meta Details about the contact modification.
     */
    protected contactModified = (meta: Object) => {
        this.readContact()
    }
}
